/* eslint-disable max-lines */
/* eslint-disable no-restricted-syntax */
import { useThemeContext } from '@utils/providers/theme-context.tsx';
import { Text, Heading } from '@components/atoms/ui-kit/text';
import { Card } from '@components/atoms/ui-kit/card';
import { EmptyState } from '@components/atoms/ui-kit/empty-state';
import { Skeleton, SkeletonBar } from '@components/atoms/ui-kit/skeleton';
import Button from '@components/atoms/ui-kit/button';
import { StatusBadge } from '@components/atoms/ui-kit/badge';
import { FileText, Users, Upload } from 'lucide-react';

export default function DesignTokensPreview() {
  const { getColour, getBrandTint, safeBrandColor, theme, toggleTheme } =
    useThemeContext();

  const brandTint = getBrandTint(0.08);
  // Fallback aligned to --color-brand / activeText.light (#0b57d0) so the demo
  // pill "All" passes WCAG AA (5.9:1 with white text) even without an org brand
  // color set. Previously #2584f5 rendered at 3.68:1 with white.
  const brand = safeBrandColor ?? '#0b57d0';

  return (
    <main className="bg-app" style={{ color: getColour('normalText') }}>
      {/* Brand-tint top bar — simulates sidebar/topbar gradient surface */}
      <div
        className="w-full px-8 py-4 flex items-center justify-between"
        style={{
          background: `linear-gradient(135deg, ${brandTint}, transparent)`,
          borderBottom: `1px solid ${getColour('hairline')}`
        }}
      >
        <h1
          style={{
            color: getColour('normalText'),
            fontWeight: 600,
            fontSize: 'inherit',
            margin: 0
          }}
        >
          Design Tokens Preview — #{1882}
        </h1>
        <div className="flex items-center gap-3">
          <span style={{ color: getColour('inactiveText'), fontSize: 13 }}>
            brand: {brand}
          </span>
          <button
            onClick={() => toggleTheme(theme === 'light')}
            className="rounded-full px-4 py-1.5 text-sm font-medium"
            style={{
              background: getColour('thirdLayer'),
              border: `1px solid ${getColour('border')}`,
              color: getColour('normalText')
            }}
          >
            {theme === 'light' ? 'Dark mode' : 'Light mode'}
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-8 py-10 flex flex-col gap-10">
        {/* ── Color tokens ──────────────────────────────────── */}
        <section>
          <h2
            className="text-sm font-semibold uppercase tracking-widest mb-4"
            style={{ color: getColour('inactiveText') }}
          >
            Color tokens
          </h2>
          <div className="flex gap-4 flex-wrap">
            {(
              [
                ['--color-app', 'bg-app', getColour('appBackground')],
                ['--color-app-soft', 'bg-app-soft', getColour('appSoft')],
                ['--color-hairline', 'border-hairline', getColour('hairline')]
              ] as [string, string, string][]
            ).map(([token, cls, value]) => (
              <div
                key={token}
                className="rounded-2xl shadow-card bg-app-soft flex flex-col gap-2 p-4 min-w-44"
              >
                <div
                  className="h-10 rounded-2xl border border-hairline"
                  style={{ background: value }}
                />
                <span
                  className="text-xs font-mono"
                  style={{ color: getColour('activeText') }}
                >
                  {token}
                </span>
                <span
                  className="text-xs"
                  style={{ color: getColour('inactiveText') }}
                >
                  {cls} · {value}
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* ── Brand palette (#2044) ─────────────────────────── */}
        <section>
          <h2
            className="text-sm font-semibold uppercase tracking-widest mb-4"
            style={{ color: getColour('inactiveText') }}
          >
            Brand palette — §2.2
          </h2>
          <div className="flex gap-3 flex-wrap">
            {(
              [
                ['brand', '--color-brand', getColour('brand')],
                ['brand2', '--color-brand-2', getColour('brand2')],
                ['brandSoft', '--color-brand-soft', getColour('brandSoft')],
                [
                  'accentForeground',
                  '--color-accent-fg',
                  getColour('accentForeground')
                ],
                ['success', '--color-success', getColour('success')],
                [
                  'successSoft',
                  '--color-success-soft',
                  getColour('successSoft')
                ],
                ['danger', '--color-danger', getColour('danger')],
                ['dangerSoft', '--color-danger-soft', getColour('dangerSoft')],
                ['borderSoft', '--color-border-soft', getColour('borderSoft')],
                [
                  'borderStrong',
                  '--color-border-strong',
                  getColour('borderStrong')
                ]
              ] as [string, string, string][]
            ).map(([token, cssVar, value]) => (
              <div
                key={token}
                className="rounded-2xl shadow-card bg-app-soft flex flex-col gap-2 p-4 min-w-44"
              >
                <div
                  className="h-10 rounded-xl border border-hairline"
                  style={{ background: value }}
                />
                <Text
                  size="xs"
                  tone="brand"
                  style={{ fontFamily: 'var(--font-mono)' }}
                >
                  {cssVar}
                </Text>
                <Text size="xs" tone="muted">
                  {token} · {value}
                </Text>
              </div>
            ))}
            {/* gradient-brand swatch */}
            <div className="rounded-2xl shadow-card bg-app-soft flex flex-col gap-2 p-4 min-w-44">
              <div className="h-10 rounded-xl bg-gradient-brand" />
              <Text
                size="xs"
                tone="brand"
                style={{ fontFamily: 'var(--font-mono)' }}
              >
                --gradient-brand
              </Text>
              <Text size="xs" tone="muted">
                linear-gradient(135deg, brand→brand-2)
              </Text>
            </div>
          </div>
        </section>

        {/* ── Shadow tokens ─────────────────────────────────── */}
        <section>
          <h2
            className="text-sm font-semibold uppercase tracking-widest mb-4"
            style={{ color: getColour('inactiveText') }}
          >
            Shadow tokens
          </h2>
          <div className="flex gap-6 flex-wrap">
            {(
              [
                ['shadow-card', 'Card'],
                ['shadow-card-hover', 'Card hover'],
                ['shadow-pop', 'Popover / modal'],
                ['shadow-soft', 'Soft']
              ] as [string, string][]
            ).map(([cls, label]) => (
              <div
                key={cls}
                className={`bg-app-soft rounded-2xl p-5 min-w-36 flex flex-col gap-1 ${cls}`}
              >
                <span
                  className="text-sm font-medium"
                  style={{ color: getColour('normalText') }}
                >
                  {label}
                </span>
                <span
                  className="text-xs font-mono"
                  style={{ color: getColour('inactiveText') }}
                >
                  {cls}
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* ── Brand-tint surface ────────────────────────────── */}
        <section>
          <h2
            className="text-sm font-semibold uppercase tracking-widest mb-4"
            style={{ color: getColour('inactiveText') }}
          >
            Brand-tint surface · getBrandTint(0.08)
          </h2>
          <div
            className="rounded-2xl shadow-card p-6"
            style={{
              background: `linear-gradient(135deg, ${getBrandTint(0.08)}, ${getBrandTint(0.03)})`
            }}
          >
            <p style={{ color: getColour('normalText') }}>
              Top-bar / sidebar gradient surface — adjusts automatically per
              reseller brand color.
            </p>
            <p
              className="text-xs mt-1"
              style={{ color: getColour('inactiveText') }}
            >
              Current brand tint: {brandTint}
            </p>
          </div>
        </section>

        {/* ── Buttons — #2049 ───────────────────────────────── */}
        <section>
          <h2
            className="text-sm font-semibold uppercase tracking-widest mb-4"
            style={{ color: getColour('inactiveText') }}
          >
            Button variants — §4
          </h2>

          {/* Variants × sizes */}
          {(['primary', 'secondary', 'ghost', 'danger', 'link'] as const).map(
            variant => (
              <div key={variant} className="mb-4">
                <Text
                  size="xs"
                  tone="muted"
                  weight="medium"
                  className="mb-2"
                  style={{ fontFamily: 'var(--font-mono)' }}
                >
                  {variant}
                </Text>
                <div className="flex gap-3 flex-wrap items-center">
                  <Button variant={variant} size="sm" label="Small" />
                  <Button variant={variant} size="md" label="Medium" />
                  <Button variant={variant} size="lg" label="Large" />
                  <Button
                    variant={variant}
                    size="md"
                    label="With icon"
                    icon={<Upload className="h-4 w-4" />}
                  />
                  <Button variant={variant} size="md" label="Loading" loading />
                  <Button
                    variant={variant}
                    size="md"
                    label="Disabled"
                    disabled
                  />
                </div>
              </div>
            )
          )}
        </section>

        {/* ── Filter chips ──────────────────────────────────── */}
        <section>
          <h2
            className="text-sm font-semibold uppercase tracking-widest mb-4"
            style={{ color: getColour('inactiveText') }}
          >
            Filter chips (pill-shaped)
          </h2>
          <div className="flex gap-2 flex-wrap">
            {['All', 'Pending', 'In progress', 'Done', 'Archived'].map(
              (label, i) => (
                <span
                  key={label}
                  className="rounded-full px-3 py-1 text-sm"
                  style={
                    i === 0
                      ? { background: brand, color: '#ffffff' }
                      : {
                          background: getColour('thirdLayer'),
                          border: `1px solid ${getColour('border')}`,
                          color: getColour('normalText')
                        }
                  }
                >
                  {label}
                </span>
              )
            )}
          </div>
        </section>

        {/* ── StatusBadge — #2050 ───────────────────────────── */}
        <section>
          <h2
            className="text-sm font-semibold uppercase tracking-widest mb-4"
            style={{ color: getColour('inactiveText') }}
          >
            StatusBadge — single status→color map
          </h2>
          <div className="flex flex-col gap-3">
            <div>
              <Text size="xs" tone="muted" weight="medium" className="mb-2">
                Session / job statuses
              </Text>
              <div className="flex gap-2 flex-wrap">
                {[
                  'Finished',
                  'Processing',
                  'Error',
                  'Pending',
                  'Cancelled'
                ].map(s => (
                  <StatusBadge key={s} status={s} />
                ))}
              </div>
            </div>
            <div>
              <Text size="xs" tone="muted" weight="medium" className="mb-2">
                Plan / billing statuses
              </Text>
              <div className="flex gap-2 flex-wrap">
                {[
                  'Active',
                  'Trial',
                  'Suspended',
                  'Inactive',
                  'Past_due',
                  'Expired'
                ].map(s => (
                  <StatusBadge key={s} status={s} />
                ))}
              </div>
            </div>
            <div>
              <Text size="xs" tone="muted" weight="medium" className="mb-2">
                Document types
              </Text>
              <div className="flex gap-2 flex-wrap">
                {['Transcript', 'Caption', 'Alignment'].map(s => (
                  <StatusBadge key={s} status={s} />
                ))}
              </div>
            </div>
            <div>
              <Text size="xs" tone="muted" weight="medium" className="mb-2">
                Tone override (tone prop)
              </Text>
              <div className="flex gap-2 flex-wrap">
                {(
                  [
                    'success',
                    'warn',
                    'danger',
                    'info',
                    'neutral',
                    'purple',
                    'orange'
                  ] as const
                ).map(t => (
                  <StatusBadge key={t} status={t} tone={t} label={t} />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── Table row with hairline dividers ──────────────── */}
        <section>
          <h2
            className="text-sm font-semibold uppercase tracking-widest mb-4"
            style={{ color: getColour('inactiveText') }}
          >
            Table — hairline dividers + hover tint
          </h2>
          <div
            className="rounded-2xl shadow-card overflow-hidden"
            style={{ border: `1px solid ${getColour('hairline')}` }}
          >
            {['Session A', 'Session B', 'Session C'].map((row, i) => (
              <div
                key={row}
                className="flex items-center px-5 py-3 gap-4"
                style={{
                  background:
                    i === 1 ? getBrandTint(0.04) : getColour('appSoft'),
                  borderTop:
                    i > 0 ? `1px solid ${getColour('hairline')}` : undefined
                }}
              >
                <span
                  className="w-48 text-sm"
                  style={{ color: getColour('normalText') }}
                >
                  {row}
                </span>
                <span
                  className="text-xs"
                  style={{ color: getColour('inactiveText') }}
                >
                  00:04:12
                </span>
                <span
                  className="ml-auto rounded-full px-2.5 py-0.5 text-xs font-medium"
                  style={{
                    color: getColour('success'),
                    background: getColour('successSoft')
                  }}
                >
                  Done
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* ── Popover / modal surface ───────────────────────── */}
        <section>
          <h2
            className="text-sm font-semibold uppercase tracking-widest mb-4"
            style={{ color: getColour('inactiveText') }}
          >
            Popover / modal surface (shadow-pop)
          </h2>
          <div
            className="rounded-2xl shadow-pop p-6 max-w-sm"
            style={{
              background: getColour('thirdLayer'),
              border: `1px solid ${getColour('border')}`
            }}
          >
            <p
              className="font-medium mb-2"
              style={{ color: getColour('normalText') }}
            >
              Modal title
            </p>
            <p className="text-sm" style={{ color: getColour('inactiveText') }}>
              This surface uses shadow-pop and rounded-2xl.
            </p>
            <div
              className="mt-4 pt-4 flex gap-2 justify-end"
              style={{ borderTop: `1px solid ${getColour('hairline')}` }}
            >
              <button
                className="rounded-full px-4 py-1.5 text-sm"
                style={{
                  border: `1px solid ${getColour('border')}`,
                  color: getColour('normalText'),
                  background: 'transparent'
                }}
              >
                Cancel
              </button>
              <button
                className="rounded-full px-4 py-1.5 text-sm font-medium"
                style={{
                  background: getColour('normalText'),
                  color: getColour('thirdLayer')
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </section>

        {/* ── Card primitive ────────────────────────────────── */}
        <section>
          <h2
            className="text-sm font-semibold uppercase tracking-widest mb-4"
            style={{ color: getColour('inactiveText') }}
          >
            Card primitive — §4
          </h2>
          <div className="flex flex-col gap-4">
            {/* Plain card */}
            <div className="flex gap-4 flex-wrap items-start">
              <Card className="flex-1 min-w-64">
                <Text weight="medium">Plain card</Text>
                <Text as="p" size="sm" tone="muted" className="mt-1">
                  Default: rounded-2xl shadow-card bg-app-soft border
                  border-hairline p-6
                </Text>
              </Card>

              <Card padding="sm" className="flex-1 min-w-64">
                <Text weight="medium">padding=&quot;sm&quot;</Text>
                <Text as="p" size="sm" tone="muted" className="mt-1">
                  p-4 body padding
                </Text>
              </Card>

              <Card padding="lg" className="flex-1 min-w-64">
                <Text weight="medium">padding=&quot;lg&quot;</Text>
                <Text as="p" size="sm" tone="muted" className="mt-1">
                  p-8 body padding
                </Text>
              </Card>
            </div>

            {/* Card with header + footer */}
            <Card
              header={
                <div className="flex items-center justify-between">
                  <Heading level={4}>Card with header</Heading>
                  <Text size="xs" tone="muted">
                    optional slot
                  </Text>
                </div>
              }
              footer={
                <div className="flex justify-end gap-2">
                  <Text size="sm" tone="muted">
                    Footer action area
                  </Text>
                </div>
              }
            >
              <Text as="p" size="sm" tone="muted">
                Header and footer get px-6 py-4 with a hairline divider. The
                body padding is controlled independently via the padding prop.
              </Text>
            </Card>

            {/* Interactive card */}
            <div className="flex gap-4 flex-wrap">
              <Card interactive className="flex-1 min-w-64 select-none">
                <Text weight="medium" tone="brand">
                  Interactive card
                </Text>
                <Text as="p" size="sm" tone="muted" className="mt-1">
                  Hover to see shadow-card-hover + cursor-pointer.
                </Text>
              </Card>

              <Card padding="none" className="flex-1 min-w-64 overflow-hidden">
                <div className="bg-gradient-brand px-6 py-4">
                  <Text weight="semibold" tone="inverse">
                    padding=&quot;none&quot;
                  </Text>
                </div>
                <div className="p-6">
                  <Text as="p" size="sm" tone="muted">
                    Full bleed header using padding=&quot;none&quot; + manual
                    inner div.
                  </Text>
                </div>
              </Card>
            </div>
          </div>
        </section>

        {/* ── EmptyState primitive ──────────────────────────── */}
        <section>
          <h2
            className="text-sm font-semibold uppercase tracking-widest mb-4"
            style={{ color: getColour('inactiveText') }}
          >
            EmptyState primitive — §4
          </h2>
          <div className="flex gap-4 flex-wrap">
            <Card padding="none" className="flex-1 min-w-72">
              <EmptyState
                title="No sessions yet"
                subtitle="Upload a file to get started with transcription."
              />
            </Card>
            <Card padding="none" className="flex-1 min-w-72">
              <EmptyState
                icon={<FileText className="h-7 w-7" />}
                title="No documents found"
                subtitle="Try adjusting your search or filters."
                action={
                  <button className="rounded-full px-4 py-1.5 text-sm font-medium text-brand bg-brand-soft">
                    Clear filters
                  </button>
                }
              />
            </Card>
            <Card padding="none" className="flex-1 min-w-72">
              <EmptyState
                icon={<Users className="h-7 w-7" />}
                title="No team members"
              />
            </Card>
          </div>
        </section>

        {/* ── Skeleton primitive ────────────────────────────── */}
        <section>
          <h2
            className="text-sm font-semibold uppercase tracking-widest mb-4"
            style={{ color: getColour('inactiveText') }}
          >
            Skeleton primitive — §4
          </h2>
          <div className="flex flex-col gap-4">
            {/* Row */}
            <Card>
              <Text size="xs" tone="muted" weight="medium" className="mb-3">
                variant=&quot;row&quot; — single bar
              </Text>
              <Skeleton variant="row" />
            </Card>

            {/* Card */}
            <Card>
              <Text size="xs" tone="muted" weight="medium" className="mb-3">
                variant=&quot;card&quot; — content placeholder
              </Text>
              <Skeleton variant="card" />
            </Card>

            {/* Table */}
            <Card padding="none">
              <div className="px-6 py-4 border-b border-hairline">
                <Text size="xs" tone="muted" weight="medium">
                  variant=&quot;table&quot; rows={5} — replaces loading… text
                </Text>
              </div>
              <div className="p-6">
                <Skeleton variant="table" rows={5} />
              </div>
            </Card>

            {/* SkeletonBar — custom composition */}
            <Card>
              <Text size="xs" tone="muted" weight="medium" className="mb-3">
                SkeletonBar — compose custom layouts
              </Text>
              <div className="flex items-center gap-3">
                <SkeletonBar className="h-10 w-10 rounded-full" />
                <div className="flex-1 flex flex-col gap-2">
                  <SkeletonBar className="h-4 w-3/4" />
                  <SkeletonBar className="h-3 w-1/2" />
                </div>
              </div>
            </Card>
          </div>
        </section>

        {/* ── Active accent + link colors ───────────────────── */}
        <section>
          <h2
            className="text-sm font-semibold uppercase tracking-widest mb-4"
            style={{ color: getColour('inactiveText') }}
          >
            Accent + link colors
          </h2>
          <div className="flex gap-6 items-center">
            <span style={{ color: getColour('activeText') }}>
              activeText (sidebar accent, links)
            </span>
            <span style={{ color: getColour('inactiveText') }}>
              inactiveText
            </span>
            <span style={{ color: getColour('normalText') }}>normalText</span>
            <span style={{ color: brand }}>brand color</span>
          </div>
        </section>

        {/* ── Typography — <Text> / <Heading> ──────────────── */}
        <section>
          <h2
            className="text-sm font-semibold uppercase tracking-widest mb-6"
            style={{ color: getColour('inactiveText') }}
          >
            Typography primitives — &lt;Text&gt; / &lt;Heading&gt;
          </h2>

          {/* Headings row */}
          <div
            className="rounded-2xl shadow-card p-6 mb-4 flex flex-col gap-3"
            style={{
              background: getColour('appSoft'),
              border: `1px solid ${getColour('hairline')}`
            }}
          >
            <Text size="xs" tone="muted" weight="medium">
              Headings (Urbanist / display font)
            </Text>
            <Text as="div" display size="3xl" weight="semibold">
              Heading 1 — Urbanist 800
            </Text>
            <Heading level={2}>Heading 2 — Urbanist 600</Heading>
            <Heading level={3}>Heading 3 — Urbanist 600</Heading>
            <Heading level={4}>Heading 4 — Urbanist 600</Heading>
          </div>

          {/* Size scale */}
          <div
            className="rounded-2xl shadow-card p-6 mb-4 flex flex-col gap-2"
            style={{
              background: getColour('appSoft'),
              border: `1px solid ${getColour('hairline')}`
            }}
          >
            <Text size="xs" tone="muted" weight="medium">
              Body size scale (Epilogue)
            </Text>
            {(['xs', 'sm', 'base', 'lg', 'xl', '2xl', '3xl'] as const).map(
              s => (
                <div key={s} className="flex items-baseline gap-3">
                  <Text
                    size="xs"
                    tone="muted"
                    style={{ minWidth: 36, fontFamily: 'var(--font-mono)' }}
                  >
                    {s}
                  </Text>
                  <Text size={s}>The quick brown fox — {s}</Text>
                </div>
              )
            )}
          </div>

          {/* Weight scale */}
          <div
            className="rounded-2xl shadow-card p-6 mb-4 flex flex-col gap-2"
            style={{
              background: getColour('appSoft'),
              border: `1px solid ${getColour('hairline')}`
            }}
          >
            <Text size="xs" tone="muted" weight="medium">
              Weight scale
            </Text>
            {(['regular', 'medium', 'semibold', 'bold'] as const).map(w => (
              <div key={w} className="flex items-center gap-3">
                <Text
                  size="xs"
                  tone="muted"
                  style={{ minWidth: 72, fontFamily: 'var(--font-mono)' }}
                >
                  {w}
                </Text>
                <Text size="base" weight={w}>
                  Epilogue weight — {w}
                </Text>
              </div>
            ))}
          </div>

          {/* Tone scale */}
          <div
            className="rounded-2xl shadow-card p-6 flex flex-col gap-2"
            style={{
              background: getColour('appSoft'),
              border: `1px solid ${getColour('hairline')}`
            }}
          >
            <Text size="xs" tone="muted" weight="medium">
              Tone scale
            </Text>
            {(['strong', 'muted', 'brand', 'danger', 'success'] as const).map(
              t => (
                <div key={t} className="flex items-center gap-3">
                  <Text
                    size="xs"
                    tone="muted"
                    style={{ minWidth: 72, fontFamily: 'var(--font-mono)' }}
                  >
                    {t}
                  </Text>
                  <Text size="base" tone={t}>
                    Text with tone=&quot;{t}&quot;
                  </Text>
                </div>
              )
            )}
            <div
              className="flex items-center gap-3 rounded-lg px-3 py-2"
              style={{ background: getColour('normalText') }}
            >
              <Text
                size="xs"
                style={{
                  minWidth: 72,
                  fontFamily: 'var(--font-mono)',
                  color: getColour('appBackground')
                }}
              >
                inverse
              </Text>
              <Text size="base" tone="inverse">
                Text with tone=&quot;inverse&quot; (on dark bg)
              </Text>
            </div>
          </div>
        </section>

        {/* ── Brand fonts info ──────────────────────────────── */}
        <section>
          <h2
            className="text-sm font-semibold uppercase tracking-widest mb-4"
            style={{ color: getColour('inactiveText') }}
          >
            Brand fonts
          </h2>
          <div className="flex gap-4 flex-wrap">
            {(
              [
                [
                  '--font-display',
                  'Urbanist',
                  'Display / headings — weights 400–800'
                ],
                ['--font-body', 'Epilogue', 'Body / UI text — weights 400–600'],
                ['--font-mono', 'ui-monospace', 'Mono — code, IDs, tokens']
              ] as const
            ).map(([token, name, desc]) => (
              <div
                key={token}
                className="rounded-2xl shadow-card bg-app-soft p-5 flex flex-col gap-1 min-w-52"
              >
                <Text
                  size="lg"
                  weight="semibold"
                  display={token === '--font-display'}
                >
                  {name}
                </Text>
                <Text
                  size="xs"
                  tone="muted"
                  style={{ fontFamily: 'var(--font-mono)' }}
                >
                  {token}
                </Text>
                <Text size="sm" tone="muted">
                  {desc}
                </Text>
              </div>
            ))}
          </div>
        </section>
      </div>
    </main>
  );
}
